/**
 * The table, shared by vs-computer and online play (docs/REDESIGN.md 7). It
 * receives a redacted state and seat info, works out legal targets with
 * legalActions(), and explains itself with light rather than words: legal
 * targets glow, everything else dims, cards fly. One short status line shows
 * only for the exceptions; screen readers get the full story in a live region.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';
import { suggestAction } from '../../engine';
import type { Action, GameState, Suggestion } from '../../engine';
import { IDLE_WARN_MS } from '../../worker/protocol';
import type { Emote } from '../../worker/protocol';
import { STICKER_LABELS, fmt, worldFor } from '../brand';
import { PlayerMedallion } from '../components/Art';
import { HowToPlay } from '../components/HowToPlay';
import { PeekCard, usePeek } from '../components/Peek';
import { Sheet } from '../components/Sheet';
import { useToast } from '../components/Toasts';
import { IconLaurel } from '../components/Icons';
import { HintBubble } from '../components/table/HintBubble';
import { Coach } from '../components/table/Coach';
import { LastCardTip } from '../components/table/LastCardTip';
import type { CoachStep } from '../components/table/Coach';
import { GameOver } from '../components/table/GameOver';
import { HoleEndSheet } from '../components/table/HoleEndSheet';
import { MyGrid } from '../components/table/MyGrid';
import { OpponentTile } from '../components/table/OpponentTile';
import { Piles } from '../components/table/Piles';
import { Scorecard } from '../components/table/Scorecard';
import { Sticker, StickerButton } from '../components/table/Stickers';
import { TableMenu } from '../components/table/TableMenu';
import { TopBar } from '../components/table/TopBar';
import { Travel } from '../components/table/Travel';
import type { TableGuide } from '../game/guide';
import { hintAvailable, hintKey, hintTarget, hintText } from '../game/hints';
import { computeLayout } from '../game/layout';
import type { SafeArea } from '../game/layout';
import type { SeatInfo, TableHandlers, TableModel } from '../game/types';
import { EMPTY_TIP_MEMORY, lastCardMoment, normalizeTipMemory, tipAllowed, tipDismissed, tipSeen, tipTurn } from '../game/lastCardTip';
import type { TipMemory } from '../game/lastCardTip';
import { usePacedState } from '../game/usePacedState';
import {
  clockSecs,
  columnChips,
  eventsSince,
  faceUp,
  lastMoveText,
  opponentOrder,
  pairedColumns,
  revealDelays,
  statusFor,
  statusLine,
  targetsFor,
  totalOf,
  withClock,
} from '../game/viewModel';
import { captureRects, playFlights, settleFlights } from '../lib/flights';
import type { Rects } from '../lib/flights';
import { coarsePointer, haptic, reducedMotion } from '../lib/motion';
import { isMuted, onMuteChange, setMuted, sfx, unlockAudio } from '../lib/sound';
import { KEYS, load, remove, save } from '../lib/storage';

type EndStage = 'none' | 'reveal' | 'sheet' | 'results';

export interface IncomingEmote {
  id: number;
  seat: number;
  emote: Emote;
}

const FALLBACK_SEAT: SeatInfo = { name: '?', isBot: false, connected: true, autopilot: false, ready: false };

const NO_SAFE: SafeArea = { top: 0, bottom: 0 };

/**
 * The visual viewport, plus the safe-area insets read off the .safe-probe
 * element (a custom property holding env() cannot be read directly; padding
 * resolves it).
 */
function useViewport(probe: RefObject<HTMLDivElement>) {
  const read = () => {
    let safe = NO_SAFE;
    const el = probe.current;
    if (el) {
      const cs = getComputedStyle(el);
      safe = { top: parseFloat(cs.paddingTop) || 0, bottom: parseFloat(cs.paddingBottom) || 0 };
    }
    return {
      w: window.visualViewport?.width ?? window.innerWidth,
      h: window.visualViewport?.height ?? window.innerHeight,
      safe,
    };
  };
  const [vp, setVp] = useState(read);
  useLayoutEffect(() => {
    const next = read();
    setVp((cur) => (cur.safe.top === next.safe.top && cur.safe.bottom === next.safe.bottom && cur.w === next.w && cur.h === next.h ? cur : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    let raf = 0;
    const on = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setVp(read()));
    };
    window.addEventListener('resize', on);
    window.visualViewport?.addEventListener('resize', on);
    return () => {
      window.removeEventListener('resize', on);
      window.visualViewport?.removeEventListener('resize', on);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return vp;
}

/**
 * Wall clock for the online idle-turn countdowns. Sleeps until the earliest
 * deadline enters the warning window, then ticks twice a second.
 */
function useClock(deadlines: (number | null | undefined)[]): number {
  const [now, setNow] = useState(() => Date.now());
  const key = deadlines.map((d) => d ?? '-').join(',');
  useEffect(() => {
    const ds = deadlines.filter((d): d is number => typeof d === 'number');
    if (!ds.length) return;
    let iv: ReturnType<typeof setInterval> | undefined;
    const startTicking = () => {
      setNow(Date.now());
      iv = setInterval(() => setNow(Date.now()), 500);
    };
    const wait = Math.min(...ds) - IDLE_WARN_MS - Date.now();
    const to = wait > 0 ? setTimeout(startTicking, wait) : (startTicking(), undefined);
    return () => {
      if (to) clearTimeout(to);
      if (iv) clearInterval(iv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return deadlines.some((d) => typeof d === 'number') ? now : 0;
}

const isLive = (s: GameState) => s.phase === 'playing' || s.phase === 'finalTurns';
const freshHole = (s: GameState) => s.phase === 'initialFlip' && s.players.every((p) => faceUp(p) === 0);

/** The travel scene for a hole, or null when the player turned travel scenes off. */
function travelFor(s: GameState): { hole: number; flavor: boolean; world: number } | null {
  if (load<boolean>(KEYS.skipTravel, false)) return null;
  const w = worldFor(s.hole, s.totalHoles);
  return { hole: s.hole, world: w.n, flavor: !load<number[]>(KEYS.worldsSeen, []).includes(w.n) };
}

function holeText(s: GameState): string {
  return s.hole > s.totalHoles ? `Playoff ${s.hole - s.totalHoles}` : `Hole ${s.hole} of ${s.totalHoles}`;
}

export function TableScreen({
  model,
  handlers,
  incomingEmotes = [],
  connectionNote,
  guide,
}: {
  model: TableModel;
  handlers: TableHandlers;
  incomingEmotes?: IncomingEmote[];
  connectionNote?: string | null;
  /** A scripted lesson driving this table (the tutorial; game/guide.ts). */
  guide?: TableGuide;
}) {
  const { me, seats, mode, isHost } = model;
  const state = usePacedState(model.state, mode === 'online' ? 420 : 0, me);
  const toast = useToast();
  const root = useRef<HTMLDivElement>(null);
  const probe = useRef<HTMLDivElement>(null);
  const vp = useViewport(probe);
  const guideRef = useRef(guide);
  guideRef.current = guide;
  const guided = !!guide;

  const hasSelf = me !== null;
  const opponents = useMemo(() => opponentOrder(state.players.length, me), [state.players.length, me]);
  const layout = computeLayout(vp.w, vp.h, opponents.length, hasSelf, vp.safe);

  const [muted, setMutedState] = useState(isMuted());
  useEffect(() => onMuteChange(setMutedState), []);
  const [tapTwice, setTapTwice] = useState<boolean>(() => load(KEYS.tapTwice, coarsePointer()));
  const [aimed, setAimed] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [shake, setShake] = useState<{ target: string; n: number } | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [stickers, setStickers] = useState<Record<number, { id: number; emote: Emote }>>({});
  const [slotFlash, setSlotFlash] = useState<Map<number, Set<number>>>(new Map());
  const [endStage, setEndStage] = useState<EndStage>(() => (state.phase === 'holeOver' || state.phase === 'gameOver' ? 'sheet' : 'none'));
  const [revealMap, setRevealMap] = useState<Map<string, number> | null>(null);
  /** Pairs that just formed, per seat: column → ms before the one-time shimmer. */
  const [forming, setForming] = useState<Map<number, Map<number, number>>>(new Map());
  const [menuOpen, setMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [scoresOpen, setScoresOpen] = useState(false);
  const [travel, setTravel] = useState<{ hole: number; flavor: boolean; world: number } | null>(() => (freshHole(state) && !guided ? travelFor(state) : null));

  const seq = useRef(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(setTimeout(fn, ms));
  }, []);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // A guided lesson narrows the legal targets to the move it teaches; everything below uses the result.
  const targets = useMemo(() => {
    const t = targetsFor(state, me);
    return guide ? guide.filterTargets(t, state) : t;
  }, [state, me, guide]);
  const mine = me !== null ? state.players[me] : undefined;
  const myTurn = me !== null && isLive(state) && state.current === me;

  // ---------------------------------------------------------------- travel
  const startTravel = useCallback((s: GameState) => setTravel(guideRef.current ? null : travelFor(s)), []);
  const endTravel = useCallback(() => {
    setTravel((t) => {
      if (t?.flavor) {
        const seen = load<number[]>(KEYS.worldsSeen, []);
        if (!seen.includes(t.world)) save(KEYS.worldsSeen, [...seen, t.world]);
      }
      return null;
    });
  }, []);

  // ------------------------------------------------------------ status flash
  const flashId = useRef(0);
  const flashLine = useCallback(
    (text: string, ms = 2500) => {
      const id = ++flashId.current;
      setFlash(text);
      later(() => setFlash((cur) => (flashId.current === id ? null : cur)), ms);
    },
    [later],
  );

  const showSticker = useCallback(
    (seat: number, emote: Emote) => {
      const id = ++seq.current;
      setStickers((m) => ({ ...m, [seat]: { id, emote } }));
      sfx.pop();
      later(() => setStickers((m) => (m[seat]?.id === id ? (({ [seat]: _, ...rest }) => rest)(m) : m)), 2000);
    },
    [later],
  );

  // ---------------------------------------------------------------- coach
  const [coachOn, setCoachOn] = useState(() => mode === 'solo' && me !== null && !guided && !load(KEYS.coach, false) && !load(KEYS.holeDone, false));
  const coachDone = useRef(new Set<number>());
  const coachShown = useRef<number | null>(null);
  const myTurnsEnded = useRef(0);
  const [coach4, setCoach4] = useState<number | null>(null); // column to spotlight, or -1 for the grid

  // --------------------------------------------------------- event handling
  const slotFlashId = useRef(0);
  const formingId = useRef(0);
  const prevRef = useRef<GameState | null>(null);
  const rectsRef = useRef<Rects>(new Map());

  useLayoutEffect(() => {
    const prev = prevRef.current;
    prevRef.current = state;
    settleFlights();
    const after = captureRects(root.current);
    const before = rectsRef.current;
    rectsRef.current = after;
    if (!prev || state.eventSeq < prev.eventSeq) {
      setEndStage(state.phase === 'holeOver' || state.phase === 'gameOver' ? 'sheet' : 'none');
      setRevealMap(null);
      if (prev && freshHole(state)) startTravel(state);
      return;
    }
    if (state.eventSeq === prev.eventSeq) return;
    const events = eventsSince(state, prev.eventSeq);
    playFlights(root.current, before, after, events, reducedMotion());

    let playedFlip = false;
    const flashes = new Map<number, Set<number>>();
    const mark = (p: number, slot: number) => {
      if (!flashes.has(p)) flashes.set(p, new Set());
      flashes.get(p)!.add(slot);
    };
    for (const e of events) {
      switch (e.type) {
        case 'holeStarted':
          sfx.deal(8);
          flashId.current++;
          setFlash(null);
          setRevealMap(null);
          setEndStage('none');
          startTravel(state);
          break;
        case 'tiebreakStarted':
          flashLine('Sudden-death playoff', 3000);
          break;
        case 'flippedInitial':
        case 'flipped':
          if (!playedFlip) sfx.flip();
          playedFlip = true;
          if (e.type === 'flipped') mark(e.player, e.slot);
          if (e.card.value === -5) {
            sfx.sparkle();
            if (e.player === me) haptic([30, 40, 30]);
          }
          break;
        case 'drewDeck':
          sfx.flick();
          break;
        case 'drewDiscard':
        case 'discarded':
          sfx.flick();
          break;
        case 'swapped':
          sfx.tap();
          mark(e.player, e.slot);
          if (e.player === me) haptic(12);
          break;
        case 'wentOut':
          sfx.out();
          flashLine(e.player === me ? 'You’re out' : 'Last shot');
          break;
        case 'finalRound':
          if (e.reason !== 'wentOut') flashLine('Last lap. One more each.', 3000);
          break;
        case 'revealed': {
          const delays = revealDelays(state, e.cards, me);
          const max = Math.max(0, ...delays.values());
          setRevealMap(delays);
          setEndStage('reveal');
          later(() => sfx.plonk(), 200);
          later(() => setEndStage((s) => (s === 'reveal' ? 'sheet' : s)), (reducedMotion() ? 300 : max) + 1200);
          break;
        }
        case 'holeScored':
          if (me !== null) save(KEYS.holeDone, true);
          break;
        default:
          break;
      }
    }
    if (flashes.size) {
      setSlotFlash(flashes);
      const id = ++slotFlashId.current;
      later(() => setSlotFlash((m) => (slotFlashId.current === id ? new Map() : m)), 1200);
    }

    // Pairs made this update: during play, and as the hole-end reveal turns cards over.
    const revealEv = events.find((e) => e.type === 'revealed');
    if (isLive(state) || revealEv) {
      const delays = revealEv && revealEv.type === 'revealed' ? revealDelays(state, revealEv.cards, me) : null;
      const fresh = new Map<number, Map<number, number>>();
      let first = Infinity;
      let last = 0;
      for (const i of state.active) {
        const was = pairedColumns(prev.players[i]?.grid ?? []);
        const now = pairedColumns(state.players[i]?.grid ?? []);
        const cols = [...now].filter((c) => !was.has(c));
        if (!cols.length) continue;
        const m = new Map<number, number>();
        for (const c of cols) {
          // My own cards flip in place at the reveal; shimmer once both have landed.
          const d = delays && i === me ? Math.max(delays.get(`${i}:${c}`) ?? 0, delays.get(`${i}:${c + 4}`) ?? 0) + 380 : 0;
          const ms = reducedMotion() ? 0 : d;
          m.set(c, ms);
          first = Math.min(first, ms);
          last = Math.max(last, ms);
        }
        fresh.set(i, m);
        if (i === me && !delays) {
          haptic([20, 40, 20]);
          if (coachOn && !coachDone.current.has(4)) setCoach4(cols[0]!);
        }
      }
      if (fresh.size) {
        // One chime per update, however many pairs formed.
        if (first > 0) later(() => sfx.pair(), first);
        else sfx.pair();
        const id = ++formingId.current;
        setForming(fresh);
        later(() => setForming((cur) => (formingId.current === id ? new Map() : cur)), last + 1400);
      }
    }

    // Your turn begins: a chime (it replaces "Your turn"), and "Your last shot" on a final turn.
    const nowMine = me !== null && isLive(state) && state.current === me && state.stage === 'draw';
    const wasMine = me !== null && isLive(prev) && prev.current === me && prev.stage === 'draw';
    if (nowMine && !wasMine) {
      later(() => sfx.turn(), 250);
      haptic(15);
      if (state.phase === 'finalTurns') flashLine('Your last shot');
    }
    // Your turn ended (coach step 4 after the third).
    if (me !== null && isLive(prev) && prev.current === me && (!isLive(state) || state.current !== me)) {
      myTurnsEnded.current++;
      if (coachOn && myTurnsEnded.current >= 3 && !coachDone.current.has(4)) setCoach4((c) => c ?? -1);
    }
    setAimed(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Re-measure after layout changes so the next flight starts from the right place.
  useLayoutEffect(() => {
    rectsRef.current = captureRects(root.current);
  }, [vp.w, vp.h, endStage]);

  // Tab title nudges when it's your turn and the tab is hidden.
  useEffect(() => {
    if (!myTurn || state.stage !== 'draw') return;
    if (document.visibilityState !== 'hidden') return;
    let on = false;
    const t = setInterval(() => {
      on = !on;
      document.title = on ? 'Your turn · Ancient Nine' : 'Ancient Nine';
    }, 1000);
    const back = () => {
      if (document.visibilityState === 'visible') {
        clearInterval(t);
        document.title = 'Ancient Nine';
      }
    };
    document.addEventListener('visibilitychange', back);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', back);
      document.title = 'Ancient Nine';
    };
  }, [myTurn, state.stage]);

  // Incoming reactions become stickers over the sender's tile.
  const lastEmote = useRef(0);
  useEffect(() => {
    for (const e of incomingEmotes) {
      if (e.id <= lastEmote.current) continue;
      lastEmote.current = e.id;
      showSticker(e.seat, e.emote);
    }
  }, [incomingEmotes, showSticker]);

  // ------------------------------------------------------------ the coach step
  let coachStep: CoachStep | null = null;
  if (coachOn && !guided && me !== null && !travel && endStage === 'none') {
    const done = coachDone.current;
    if (!done.has(1) && state.phase === 'initialFlip' && state.active.includes(me) && faceUp(mine) < 2) {
      coachStep = { n: 1, text: 'Flip any two of your cards.', spot: ['grid'] };
    } else if (!done.has(2) && myTurn && state.stage === 'draw') {
      coachStep = { n: 2, text: 'Take the top card from either pile.', spot: ['deck', 'discard'] };
    } else if (!done.has(3) && myTurn && state.stage === 'holding') {
      coachStep =
        state.drawnFrom === 'deck'
          ? { n: 3, text: 'Swap it into your grid, or toss it and flip one.', spot: ['grid', 'discard'] }
          : { n: 3, text: 'Swap it into any of your cards.', spot: ['grid'] };
    } else if (!done.has(4) && coach4 !== null && done.has(3)) {
      coachStep = { n: 4, text: 'Matching columns score zero. Lowest score wins.', spot: [coach4 >= 0 ? `col${coach4}` : 'grid'] };
    }
  }
  // A step that was showing and no longer applies has been acted on.
  const stepN = coachStep?.n ?? null;
  useEffect(() => {
    const was = coachShown.current;
    if (was !== null && was !== stepN && was !== 4) coachDone.current.add(was);
    coachShown.current = stepN;
  }, [stepN]);
  const finishCoach = useCallback(() => {
    save(KEYS.coach, true);
    setCoachOn(false);
  }, []);
  const replayCoach = useCallback(() => {
    remove(KEYS.coach);
    remove(KEYS.lastCardTip);
    setTipMem(EMPTY_TIP_MEMORY);
    coachDone.current = new Set();
    coachShown.current = null;
    myTurnsEnded.current = 0;
    setCoach4(null);
    setCoachOn(me !== null);
  }, [me]);

  // ------------------------------------------------------------- the hint (A1)
  const [hintsOn, setHintsOn] = useState<boolean>(() => load(KEYS.hints, true));
  const [hint, setHint] = useState<{ key: string; suggestion: Suggestion; text: string } | null>(null);
  const hintPill = hintAvailable({
    mode,
    guided,
    setting: hintsOn,
    state,
    me,
    busy: !!coachStep || !!travel || endStage !== 'none' || menuOpen || helpOpen || scoresOpen,
  });
  const shownHint = hint && hintPill && hint.key === hintKey(state) ? hint : null;
  // A hint dies with its step: any new event, a sheet, or the pill going away.
  useEffect(() => {
    if (hint && !shownHint) setHint(null);
  }, [hint, shownHint]);
  const toggleHint = () => {
    if (shownHint) return setHint(null);
    if (!hintPill || me === null) return;
    unlockAudio();
    const suggestion = suggestAction(state, me);
    if (!suggestion) return sfx.error();
    const text = hintText(suggestion);
    setHint({ key: hintKey(state), suggestion, text });
    sfx.pop();
    haptic(10);
    // Tap twice to swap: pre-aim the advised slot, so one more tap commits.
    if (suggestion.action.type === 'swap' && tapTwice) setAimed(suggestion.action.slot);
  };
  const hintMark = shownHint ? hintTarget(shownHint.suggestion) : null;

  // ------------------------------------------------------- the last-card tip
  const [tipMem, setTipMem] = useState<TipMemory>(() => normalizeTipMemory(load(KEYS.lastCardTip, null)));
  const gameId = model.gameId ?? `${mode}:${model.roomCode ?? ''}`;
  const showTip =
    !guided && !shownHint && !coachStep && !travel && endStage === 'none' && !menuOpen && !helpOpen && !scoresOpen && lastCardMoment(state, me) && tipAllowed(tipMem, gameId, tipTurn(state));
  const turnKey = tipTurn(state);
  useEffect(() => {
    if (!showTip || tipMem.turn[gameId] !== undefined) return;
    const next = tipSeen(tipMem, gameId, turnKey);
    setTipMem(next);
    save(KEYS.lastCardTip, next);
  }, [showTip, tipMem, gameId, turnKey]);
  const dismissTip = useCallback(() => {
    setTipMem((m) => {
      const next = tipDismissed(m, gameId);
      save(KEYS.lastCardTip, next);
      return next;
    });
  }, [gameId]);

  // Bots wait while a coach step or the travel scene is up (vs computer).
  const holdBots = !!coachStep || !!travel;
  useEffect(() => {
    handlers.pause?.(holdBots);
  }, [holdBots, handlers]);

  // ------------------------------------------------------------ interaction
  const reject = useCallback(
    (target: string) => {
      sfx.error();
      const n = ++seq.current;
      setShake({ target, n });
      later(() => setShake((s) => (s?.n === n ? null : s)), 320);
      guideRef.current?.onReject?.(target);
    },
    [later],
  );

  const act = useCallback(
    (a: Action) => {
      unlockAudio();
      setAimed(null);
      handlers.act(a);
    },
    [handlers],
  );

  const onDeck = () => (targets.deck && me !== null ? act({ type: 'drawDeck', player: me }) : reject('deck'));
  const onDiscard = () => {
    if (me === null) return reject('discard');
    if (targets.discard) return act({ type: 'drawDiscard', player: me });
    if (targets.throwAway) return act({ type: 'discardDrawn', player: me });
    return reject('discard');
  };
  const onToss = () => (targets.throwAway && me !== null ? act({ type: 'discardDrawn', player: me }) : reject('discard'));
  const onKeepHidden = () => (targets.keepHidden && me !== null ? act({ type: 'skip', player: me }) : undefined);
  const onSlot = (slot: number) => {
    if (me === null) return;
    if (!targets.slots.has(slot) || !targets.slotAction) return reject(`slot${slot}`);
    if (targets.slotAction === 'swap' && tapTwice && aimed !== slot) {
      setAimed(slot);
      sfx.tap();
      return;
    }
    act({ type: targets.slotAction, player: me, slot });
  };

  // Keyboard: D deck, F discard, X toss, 1..8 slots, P keep hidden, ? help.
  const keyboard = useRef(false);
  const anySheet = menuOpen || helpOpen || scoresOpen || endStage === 'sheet' || endStage === 'results' || !!travel;
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyRef.current = (e: KeyboardEvent) => {
    keyboard.current = true;
    if (anySheet || e.metaKey || e.ctrlKey || e.altKey) return;
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    const k = e.key.toLowerCase();
    if (k === 'd') onDeck();
    else if (k === 'f') onDiscard();
    else if (k === 'x') onToss();
    else if (k === 'p') onKeepHidden();
    else if (k === 'h' && (hintPill || shownHint)) toggleHint();
    else if (k === '?' && !guideRef.current) setHelpOpen(true);
    else if (k === 'escape') setAimed(null);
    else if (/^[1-8]$/.test(k)) {
      const slot = Number(k) - 1;
      document.querySelector<HTMLElement>(`[data-slot-key="${me}:${slot}"]`)?.focus({ preventScroll: true });
      onSlot(slot);
    } else return;
    e.preventDefault();
  };
  useEffect(() => {
    const on = (e: KeyboardEvent) => keyRef.current(e);
    const ptr = () => {
      keyboard.current = false;
    };
    window.addEventListener('keydown', on);
    window.addEventListener('pointerdown', ptr, true);
    return () => {
      window.removeEventListener('keydown', on);
      window.removeEventListener('pointerdown', ptr, true);
    };
  }, []);

  // Focus moves to the first legal target at each step (keyboard users; 8.9).
  const stepKey = myTurn || (state.phase === 'initialFlip' && targets.any) ? `${state.hole}:${state.phase}:${state.stage}:${state.eventSeq}` : null;
  const focusKey = keyboard.current && stepKey && targets.slots.size ? stepKey : null;
  useEffect(() => {
    if (!keyboard.current || !myTurn || state.stage !== 'draw' || anySheet) return;
    root.current?.querySelector<HTMLElement>('[data-pile="deck"]')?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  const peek = usePeek(root, !travel);

  // ------------------------------------------------------------ derived bits
  const ghostSlot = tapTwice ? aimed : state.stage === 'holding' && myTurn ? hover : null;
  const clockNow = useClock(seats.map((s) => s.turnDeadline));
  const secsFor = (i: number) => (clockNow ? clockSecs(seats[i], clockNow, IDLE_WARN_MS) : null);
  const mySecs = me !== null ? secsFor(me) : null;
  const warned = mySecs !== null;
  useEffect(() => {
    if (!warned) return;
    sfx.turn();
    haptic([20, 60, 20]);
  }, [warned]);
  const line = connectionNote ? null : statusLine(state, seats, me, clockNow, IDLE_WARN_MS, flash);
  const srStatus = withClock(statusFor(state, seats, me, { aimedSlot: tapTwice ? aimed : null }), state, seats, me, clockNow, IDLE_WARN_MS);
  const lastMove = lastMoveText(state, seats, me);
  const srText = [srStatus.text, srStatus.sub ?? (lastMove && lastMove !== srStatus.text ? lastMove : '')].filter(Boolean).join(' ');
  const myChips = mine && mine.grid.length === 8 ? columnChips(mine.grid, state.options) : null;

  const finalRoundPlayers = useMemo(() => {
    for (let i = state.log.length - 1; i >= 0; i--) {
      const e = state.log[i]!;
      if (e.type === 'finalRound') return e.players;
      if (e.type === 'holeStarted') return null;
    }
    return null;
  }, [state.log]);
  const lastShotFor = (i: number): 'owed' | 'done' | null => {
    if (state.phase !== 'finalTurns' || !finalRoundPlayers?.includes(i)) return null;
    return state.turnsLeftInFinal.includes(i) ? 'owed' : 'done';
  };

  const myFlipDelays = useMemo(() => {
    if (!revealMap || me === null) return null;
    const m = new Map<number, number>();
    for (const [k, v] of revealMap) {
      const [p, s] = k.split(':').map(Number);
      if (p === me && s !== undefined) m.set(s, v);
    }
    return m;
  }, [revealMap, me]);

  const dealing = state.phase === 'initialFlip' && faceUp(mine) === 0 && !reducedMotion();
  const sittingOut = me !== null && !state.active.includes(me);
  const teePips = me !== null && state.phase === 'initialFlip' && state.active.includes(me);

  const rootStyle = {
    '--cw': `${layout.cw}px`,
    '--mini': `${layout.mini}px`,
    '--cols': layout.cols,
    ...(layout.phone
      ? { '--tile-w': `${layout.tileW}px`, '--tile-h': `${layout.tileH}px`, '--pgap': `${layout.pgap}px`, '--gap-top': `${layout.gapTop}px`, '--gap-mid': `${layout.gapMid}px` }
      : {}),
  } as CSSProperties;


  const playAgain = () => {
    setEndStage('none');
    handlers.playAgain();
  };

  const toggleMute = () => {
    setMuted(!muted);
    if (muted) later(() => sfx.pop(), 50);
  };

  const copyLink = () => {
    if (!model.roomCode) return;
    const link = `${location.origin}/r/${model.roomCode}`;
    navigator.clipboard?.writeText(link).then(
      () => toast('Invite link copied.'),
      () => toast(link),
    );
  };

  const travelWorld = travel ? worldFor(travel.hole, state.totalHoles) : null;

  return (
    <div
      ref={root}
      className={`table-screen${hasSelf ? '' : ' watching'}${layout.side ? ' side' : ''}${layout.tight ? ' tight' : ''}${layout.wide ? ' wide' : ''}${myTurn ? ' my-turn' : ''}${
        layout.compact ? ' compact-tiles' : ''
      }${layout.phone ? ' phone' : ''}${layout.strip ? ' strip-tiles' : ''}${shownHint ? ' hinting' : ''}`}
      style={rootStyle}
      onPointerDown={() => unlockAudio()}
      onClick={() => {
        if (endStage === 'reveal') setEndStage('sheet');
      }}
    >
      <TopBar
        state={state}
        muted={muted}
        onMenu={() => (guide ? guide.onExit() : setMenuOpen(true))}
        onScores={() => setScoresOpen(true)}
        onMute={toggleMute}
        onHelp={() => setHelpOpen(true)}
        {...(model.roomCode ? { roomCode: model.roomCode } : {})}
        {...(guide ? { minimal: true, center: guide.topCenter } : {})}
      />

      <section
        className={`opponents rows-${layout.rows}`}
        aria-label="Other players"
        style={layout.phone ? undefined : { gridTemplateColumns: `repeat(${layout.cols}, minmax(0, ${layout.side ? '1fr' : `${layout.tileW}px`}))` }}
      >
        {opponents.map((i) => (
          <OpponentTile
            key={i}
            seat={i}
            state={state}
            info={seats[i] ?? { ...FALLBACK_SEAT, name: state.players[i]?.name ?? '?' }}
            compact={layout.compact}
            extras={{
              lastShot: lastShotFor(i),
              clock: secsFor(i),
              deadline: isLive(state) || state.phase === 'initialFlip' ? (seats[i]?.turnDeadline ?? null) : null,
              sticker: stickers[i] ?? null,
              flash: slotFlash.get(i) ?? EMPTY_SET,
              forming: forming.get(i) ?? EMPTY_MAP,
            }}
            narrow={layout.tileW < 150}
            {...(layout.phone ? { medal: 20, strip: layout.strip } : {})}
          />
        ))}
      </section>

      <section className="center" aria-label="Table">
        <Piles
          state={state}
          me={me}
          targets={targets}
          pw={layout.pw}
          hw={layout.hw}
          shake={shake?.target === 'deck' || shake?.target === 'discard' ? shake.target : null}
          onDeck={onDeck}
          onDiscard={onDiscard}
          onKeepHidden={onKeepHidden}
          hint={hintMark && hintMark.kind !== 'slot' ? hintMark.kind : null}
        />
        <div className="status-line" aria-hidden="true">
          {connectionNote ? (
            <span className="status-text" key="conn">
              <span className="spinner small" /> {connectionNote}
            </span>
          ) : line ? (
            <span className="status-text" key={line}>
              {line}
            </span>
          ) : null}
        </div>
      </section>

      {hasSelf && mine && (
        <section className={`mine${myTurn ? ' active' : ''}`} aria-label="Your area">
          {sittingOut ? (
            <div className="sitting-out" />
          ) : (
            <MyGrid
              key={`hole-${state.hole}`}
              grid={mine.grid}
              seat={me}
              cw={layout.cw}
              targetSlots={targets.slots}
              slotAction={targets.slotAction}
              aimed={ghostSlot}
              ghostValue={state.drawn?.value ?? null}
              chips={myChips?.chips ?? null}
              pairs={pairedColumns(mine.grid)}
              flipDelays={myFlipDelays}
              dealing={dealing}
              forming={forming.get(me) ?? EMPTY_MAP}
              flash={slotFlash.get(me) ?? EMPTY_SET}
              shakeSlot={shake?.target.startsWith('slot') ? Number(shake.target.slice(4)) : null}
              focusKey={focusKey}
              onSlot={onSlot}
              onHover={setHover}
              hintSlot={hintMark?.kind === 'slot' ? hintMark.slot : null}
            />
          )}
          <div className="mebar">
            <span className="mebar-medal">
              <PlayerMedallion seat={me} size={30} active={myTurn} />
              {mySecs !== null && <span className="mebar-clock num">0:{String(mySecs).padStart(2, '0')}</span>}
            </span>
            <span className="mebar-name">{seats[me]?.name || 'You'}</span>
            {teePips && (
              <span className="tee-pips" aria-label={`${Math.min(2, faceUp(mine))} of 2 flipped`}>
                <i className={faceUp(mine) >= 1 ? 'on' : ''} />
                <i className={faceUp(mine) >= 2 ? 'on' : ''} />
              </span>
            )}
            {hintPill && (
              <button type="button" className="hint-pill" aria-keyshortcuts="H" aria-label="Hint" aria-pressed={!!shownHint} onClick={toggleHint}>
                <IconLaurel size={16} />
                Hint
              </button>
            )}
            <span className="mebar-total num" aria-label={`Your total ${fmt(totalOf(mine))}`}>
              {fmt(totalOf(mine))}
            </span>
            {!guided && (
              <StickerButton
                onSend={(e) => {
                  handlers.emote(e);
                  if (mode === 'solo') showSticker(me, e);
                }}
              />
            )}
            {stickers[me] && <Sticker key={stickers[me]!.id} emote={stickers[me]!.emote} className="me-sticker" />}
          </div>
          {shownHint && <HintBubble key={shownHint.key} text={shownHint.text} onClose={() => setHint(null)} />}
          {showTip && <LastCardTip onClose={dismissTip} />}
        </section>
      )}
      {!hasSelf && (
        <div className="spectating">
          <span className="pill">You’re watching</span>
        </div>
      )}

      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {connectionNote ?? srText}
      </p>
      <p className="sr-only" aria-live="polite">
        {shownHint ? `Hint: ${shownHint.text}` : ''}
      </p>
      <div ref={probe} className="safe-probe" aria-hidden="true" />

      <Sheet open={endStage === 'sheet' && (state.phase === 'holeOver' || state.phase === 'gameOver')} className="holeend-sheet" labelledBy="he-title">
        <HoleEndSheet
          state={state}
          seats={seats}
          me={me}
          mode={mode}
          isHost={isHost}
          onNext={() => {
            unlockAudio();
            handlers.ready();
          }}
          {...(handlers.forceNext ? { onForce: handlers.forceNext } : {})}
          onScores={() => setScoresOpen(true)}
          onResults={() => {
            if (guide) return guide.onFinished();
            setEndStage('results');
            if (me !== null && state.winners?.includes(me)) sfx.win();
          }}
        />
      </Sheet>

      {endStage === 'results' && state.phase === 'gameOver' && !guided && (
        <GameOver
          state={state}
          seats={seats}
          me={me}
          mode={mode}
          isHost={isHost}
          onPlayAgain={playAgain}
          {...(handlers.backToLobby ? { onLobby: handlers.backToLobby } : {})}
          onHome={handlers.home}
          onScores={() => setScoresOpen(true)}
        />
      )}

      <HowToPlay open={helpOpen} onClose={() => setHelpOpen(false)} {...(me !== null ? { onTour: replayCoach } : {})} />

      <Sheet open={scoresOpen} onClose={() => setScoresOpen(false)} title="Scorecard" labelledBy="sc-title" wide className="sc-sheet">
        <Scorecard state={state} seats={seats} me={me} />
      </Sheet>

      <TableMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        mode={mode}
        isHost={isHost}
        muted={muted}
        tapTwice={tapTwice}
        {...(mode === 'solo' && !guided
          ? {
              hints: hintsOn,
              onHints: () =>
                setHintsOn((h) => {
                  save(KEYS.hints, !h);
                  return !h;
                }),
            }
          : {})}
        {...(model.roomCode ? { roomCode: model.roomCode } : {})}
        onMute={toggleMute}
        onTapTwice={() => {
          setTapTwice((t) => {
            save(KEYS.tapTwice, !t);
            return !t;
          });
        }}
        onHelp={() => setHelpOpen(true)}
        onHome={handlers.home}
        {...(handlers.quit ? { onQuit: handlers.quit } : {})}
        {...(handlers.backToLobby && isHost ? { onLobby: handlers.backToLobby } : {})}
        onCopyLink={copyLink}
      />

      {coachStep && (
        <Coach
          key={coachStep.n}
          step={coachStep}
          onSkip={finishCoach}
          onDone={() => {
            coachDone.current.add(4);
            finishCoach();
          }}
        />
      )}

      {travel && travelWorld && (
        <Travel
          key={travel.hole}
          world={travelWorld}
          holeText={holeText({ ...state, hole: travel.hole })}
          flavor={travel.flavor}
          reduced={reducedMotion()}
          onDone={endTravel}
        />
      )}

      <PeekCard peek={peek} />
      {Object.entries(stickers).map(([seat, st]) => (
        <span key={st.id} className="sr-only" role="status">
          {Number(seat) === me ? 'You' : (seats[Number(seat)]?.name ?? '')}: {STICKER_LABELS[st.emote]}
        </span>
      ))}
      {guide?.overlay}
    </div>
  );
}

const EMPTY_SET: Set<number> = new Set();
const EMPTY_MAP: Map<number, number> = new Map();
